/**
 * Every date a person reads, in one format (T21-M1).
 *
 * MM/DD/YYYY and h:mm AM/PM, in English and in Spanish alike (Jorge,
 * 2026-10-05). Storage and the wire stay ISO 8601; this module is the only
 * place that turns either into text for a screen.
 *
 * **Two kinds of value, two clocks.**
 *
 *   * A calendar date — `"2026-10-05"`, a job's preferred start, a quote's
 *     proposed end — has no time and no zone. It is read off the string as
 *     written and never passes through `Date`, because `new Date("2026-10-05")`
 *     is midnight UTC, and midnight UTC is the evening of the 4th everywhere in
 *     the United States. That was the bug on the public job board.
 *   * A timestamp — `created_at`, `sent_at` — is an instant, shown on the
 *     viewer's own clock unless the caller names another one with
 *     `{ timeZone }`. A timestamp with no offset is read as UTC, because that
 *     is what the backend stores; the browser's rule (local time) would shift
 *     it by the viewer's offset.
 *
 * Nothing here returns "Invalid Date": an empty, missing or unreadable value
 * formats to "".
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
// A date-time with no `Z` and no `±hh:mm` at the end.
const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

const pad = (n, width = 2) => String(n).padStart(width, "0");

function daysInMonth(year, month) {
  // Day 0 of the next month is the last day of this one. UTC so no zone's
  // DST rule can move it.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function validParts(year, month, day) {
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

/** `"2026-10-05"` → `{ year, month, day }`, or null when it is not a real date. */
function dateOnlyParts(value) {
  const m = DATE_ONLY.exec(value);
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return validParts(year, month, day) ? { year, month, day } : null;
}

/** Anything a caller may hand in → a valid `Date`, or null. */
function toInstant(value) {
  if (value === null || value === undefined || value === "") return null;
  let d;
  if (value instanceof Date) {
    d = value;
  } else if (typeof value === "number") {
    d = new Date(value);
  } else {
    const text = String(value).trim();
    d = new Date(NAIVE_DATE_TIME.test(text) ? `${text.replace(" ", "T")}Z` : text);
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

const formatters = new Map();
function partsOf(date, timeZone) {
  const key = timeZone || "";
  if (!formatters.has(key)) {
    formatters.set(key, new Intl.DateTimeFormat("en-US", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "numeric", minute: "2-digit", hour12: true,
      ...(timeZone ? { timeZone } : {}),
    }));
  }
  const out = {};
  for (const p of formatters.get(key).formatToParts(date)) out[p.type] = p.value;
  return out;
}

const usDate = (year, month, day) => `${pad(month)}/${pad(day)}/${pad(year, 4)}`;

/**
 * `"2026-10-05"` → `"10/05/2026"`; a timestamp → its day on the viewer's
 * clock, or on `options.timeZone` when given (`"UTC"` for a boundary the
 * server drew in UTC, such as the first of a billing month).
 */
export function formatDate(value, options = {}) {
  if (typeof value === "string") {
    const parts = dateOnlyParts(value.trim());
    if (parts) return usDate(parts.year, parts.month, parts.day);
    if (DATE_ONLY.test(value.trim())) return "";
  }
  const d = toInstant(value);
  if (!d) return "";
  const p = partsOf(d, options.timeZone);
  return usDate(Number(p.year), Number(p.month), Number(p.day));
}

/** A timestamp → `"3:05 PM"`. A calendar date has no time: `""`. */
export function formatTime(value, options = {}) {
  if (typeof value === "string" && DATE_ONLY.test(value.trim())) return "";
  const d = toInstant(value);
  if (!d) return "";
  const p = partsOf(d, options.timeZone);
  return `${p.hour}:${p.minute} ${String(p.dayPeriod).toUpperCase()}`;
}

/** A timestamp → `"10/05/2026, 3:05 PM"`. A calendar date → just the date. */
export function formatDateTime(value, options = {}) {
  const date = formatDate(value, options);
  const time = formatTime(value, options);
  return date && time ? `${date}, ${time}` : date;
}

/**
 * "just now" / "5 min ago" / "3 h ago" / "2 days ago", through the caller's
 * `t` so the words follow the language. The keys are the ones the Dany thread
 * list already had.
 */
export function formatRelative(value, t, now = Date.now()) {
  const d = toInstant(value);
  if (!d) return "";
  const minutes = Math.max(0, Math.round((now - d.getTime()) / 60000));
  if (minutes < 1) return t("dany.thread.justNow");
  if (minutes < 60) return t("dany.thread.minutesAgo", { n: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t("dany.thread.hoursAgo", { n: hours });
  return t("dany.thread.daysAgo", { n: Math.round(hours / 24) });
}

/**
 * `"10/05/2026"` (or `"10/5/2026"`) → a `Date` at local midnight of that day,
 * or null. Strict: February 30th is null, not March 2nd.
 */
export function parseUSDate(text) {
  const m = US_DATE.exec(String(text ?? "").trim());
  if (!m) return null;
  const [month, day, year] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (!validParts(year, month, day)) return null;
  const d = new Date(2000, month - 1, day);
  d.setFullYear(year); // the constructor maps years 0–99 to 1900–1999
  return d;
}

/** A local `Date` → `"YYYY-MM-DD"` of its local day, the wire form of a calendar date. */
export function toISODate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `"2026-10-05"` → a local `Date` at midnight of that day, or null. */
export function fromISODate(value) {
  const parts = typeof value === "string" ? dateOnlyParts(value.trim()) : null;
  if (!parts) return null;
  const d = new Date(2000, parts.month - 1, parts.day);
  d.setFullYear(parts.year);
  return d;
}

/** `"10/05/2026"` → `"2026-10-05"`, or `""` when it is not a real date. */
export function usDateToISO(text) {
  return toISODate(parseUSDate(text));
}
